import VoiceCallsApiClient from '../infrastructure/api/VoiceCallsApiClient';
import { hangupCurrentAgentCall } from '../infrastructure/webrtc/voiceAgentWebRtcAdapter';
import { resolveVoiceHangupEndedReason } from '../domain/voiceHangupEndedReason';
import { voiceSocketLog } from '../../../utils/voiceSocketDebug';

/** @type {Set<string>} */
const hangupInFlight = new Set();
/** @type {Set<string>} */
const hangupPosted = new Set();
/** @type {Set<string>} */
const answerAttempted = new Set();
/** @type {Set<string>} */
const answeredCallIds = new Set();
/** @type {Set<string>} */
const sipAnsweredOnce = new Set();
/** @type {Map<string, { resolve: () => void }>} */
const hangupInFlightWaiters = new Map();
/** This tab's pending POST /answer or in-call id. Not a ringing-queue pointer. */
let claimedCallControlId = null;
/**
 * Contestar in flight: POST /answer may still be open. Auto hangup from JsSIP failed/ended
 * must not race Nest (causes 409 call_ended while answer is bridging).
 */
/** @type {string|null} */
let answerBridgeInFlightId = null;
/** mediaFailed while Contestar still open — hang up after bridge clears (avoid Nest 502). */
/** @type {Set<string>} */
const pendingMediaFailedHangup = new Set();

const HANGUP_IN_FLIGHT_WAIT_MS = 15_000;

export function getVoiceTabClaimedCallControlId() {
  return claimedCallControlId;
}

/**
 * Clears module-level tracking for a call (invoked from voiceReleaseCallSession).
 *
 * @param {string|null|undefined} callControlId
 */
export function clearVoiceCallSessionTracking(callControlId) {
  if (!callControlId) {
    claimedCallControlId = null;
    answerBridgeInFlightId = null;
    return;
  }
  if (claimedCallControlId === callControlId) {
    claimedCallControlId = null;
  }
  if (answerBridgeInFlightId === callControlId) {
    answerBridgeInFlightId = null;
  }
  pendingMediaFailedHangup.delete(callControlId);
  answerAttempted.delete(callControlId);
  answeredCallIds.delete(callControlId);
  sipAnsweredOnce.delete(callControlId);
  hangupPosted.delete(callControlId);
  hangupInFlight.delete(callControlId);
  resolveHangupInFlightWaiters(callControlId);
}

/**
 * One tab, one answer. Returns false if this tab already claimed another call.
 * @param {string} callControlId
 * @returns {boolean}
 */
export function claimVoiceTabAnswer(callControlId) {
  if (!callControlId) return false;
  if (claimedCallControlId && claimedCallControlId !== callControlId) {
    return false;
  }
  claimedCallControlId = callControlId;
  answerAttempted.add(callControlId);
  return true;
}

export function isVoiceTabAnswerClaimed(callControlId) {
  return Boolean(callControlId) && claimedCallControlId === callControlId;
}

export function releaseVoiceTabAnswer(callControlId) {
  if (!callControlId || claimedCallControlId === callControlId) {
    claimedCallControlId = null;
  }
  if (callControlId) {
    if (answerBridgeInFlightId === callControlId) {
      answerBridgeInFlightId = null;
    }
    answerAttempted.delete(callControlId);
    answeredCallIds.delete(callControlId);
    sipAnsweredOnce.delete(callControlId);
    // Keep pendingMediaFailedHangup — flushed after answer bridge clears.
  }
}

/**
 * Mark Contestar / answer use case as in flight so sdk.failed cannot POST /hangup in parallel.
 * @param {string} callControlId
 */
export function markVoiceAnswerBridgeInFlight(callControlId) {
  if (callControlId) {
    answerBridgeInFlightId = callControlId;
  }
}

/**
 * @param {string|null|undefined} callControlId
 */
export function clearVoiceAnswerBridgeInFlight(callControlId) {
  if (!callControlId || answerBridgeInFlightId === callControlId) {
    answerBridgeInFlightId = null;
  }
}

/**
 * @param {string|null|undefined} callControlId
 * @returns {boolean}
 */
export function isVoiceAnswerBridgeInFlight(callControlId) {
  return Boolean(callControlId) && answerBridgeInFlightId === callControlId;
}

/**
 * Defer media-failed hangup until Contestar / POST /answer finishes.
 * @param {string} callControlId
 */
export function markPendingMediaFailedHangup(callControlId) {
  if (callControlId) {
    pendingMediaFailedHangup.add(callControlId);
  }
}

/**
 * @param {string|null|undefined} callControlId
 * @returns {boolean}
 */
export function consumePendingMediaFailedHangup(callControlId) {
  if (!callControlId || !pendingMediaFailedHangup.has(callControlId)) {
    return false;
  }
  pendingMediaFailedHangup.delete(callControlId);
  return true;
}

export function markVoiceSipAnsweredOnce(callControlId) {
  if (callControlId) {
    sipAnsweredOnce.add(callControlId);
  }
}

export function wasVoiceSipAnsweredOnce(callControlId) {
  return Boolean(callControlId) && sipAnsweredOnce.has(callControlId);
}

export function markVoiceCallAnswerAttempted(callControlId) {
  if (callControlId) {
    answerAttempted.add(callControlId);
  }
}

export function markVoiceCallAnswered(callControlId) {
  if (callControlId) {
    answeredCallIds.add(callControlId);
    answerAttempted.add(callControlId);
  }
}

export function isVoiceCallAnsweredOrAttempted(callControlId) {
  if (!callControlId) return false;
  return answeredCallIds.has(callControlId) || answerAttempted.has(callControlId);
}

export function isVoiceHangupInFlight(callControlId) {
  return Boolean(callControlId) && hangupInFlight.has(callControlId);
}

export function isVoiceHangupPosted(callControlId) {
  return Boolean(callControlId) && hangupPosted.has(callControlId);
}

/**
 * @param {{
 *   callControlId?: string|null,
 *   activeCallControlId?: string|null,
 *   outboundDialingCallControlId?: string|null
 * }} state
 * @param {string} callControlId
 */
export function resolveEndedReasonForVoiceCall(state, callControlId) {
  const wasAnswered = state?.activeCallControlId === callControlId
    || isVoiceCallAnsweredOrAttempted(callControlId);
  const isOutboundRinging = state?.outboundDialingCallControlId === callControlId
    && !wasAnswered;
  return resolveVoiceHangupEndedReason({ wasAnswered, isOutboundRinging });
}

function resolveHangupInFlightWaiters(callControlId) {
  const waiter = hangupInFlightWaiters.get(callControlId);
  if (!waiter) return;
  hangupInFlightWaiters.delete(callControlId);
  waiter.resolve();
}

function waitForHangupInFlight(callControlId, timeoutMs = HANGUP_IN_FLIGHT_WAIT_MS) {
  if (!callControlId || !hangupInFlight.has(callControlId)) {
    return Promise.resolve();
  }
  const existing = hangupInFlightWaiters.get(callControlId);
  if (existing?.promise) {
    return existing.promise;
  }

  let resolveDone;
  const promise = new Promise((resolve) => {
    resolveDone = resolve;
  });
  const timeoutId = setTimeout(() => {
    resolveHangupInFlightWaiters(callControlId);
  }, timeoutMs);

  hangupInFlightWaiters.set(callControlId, {
    promise,
    resolve: () => {
      clearTimeout(timeoutId);
      resolveDone();
    }
  });

  return promise;
}

/**
 * POST /hangup first, wait 200, then terminate JsSIP, then local reset.
 *
 * @param {{
 *   callControlId?: string|null,
 *   endedReason: string,
 *   onAfterHttpOk?: () => void
 * }} options
 */
export async function completeVoiceCallHangup({
  callControlId,
  endedReason,
  onAfterHttpOk
}) {
  if (!callControlId) return { posted: false };

  if (hangupPosted.has(callControlId)) {
    await hangupCurrentAgentCall().catch(() => {});
    onAfterHttpOk?.();
    releaseVoiceTabAnswer(callControlId);
    return { posted: true, already: true };
  }

  if (hangupInFlight.has(callControlId)) {
    await waitForHangupInFlight(callControlId);
    if (hangupPosted.has(callControlId)) {
      await hangupCurrentAgentCall().catch(() => {});
      onAfterHttpOk?.();
      releaseVoiceTabAnswer(callControlId);
      return { posted: true, already: true };
    }
    return { posted: false, inFlight: true };
  }

  hangupInFlight.add(callControlId);
  try {
    voiceSocketLog('POST hangup', { callControlId, endedReason });
    await VoiceCallsApiClient.hangupCall(callControlId, { endedReason });
    hangupPosted.add(callControlId);
    await hangupCurrentAgentCall().catch(() => {});
    onAfterHttpOk?.();
    releaseVoiceTabAnswer(callControlId);
    return { posted: true };
  } finally {
    hangupInFlight.delete(callControlId);
    resolveHangupInFlightWaiters(callControlId);
  }
}
